/**
 * Best-effort persistence for small Pinia snapshots (sessionStorage).
 * Not required for correctness: if data is cleared, lost, or writes fail, the app keeps working with defaults.
 */

function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function toPlainClone(payload: unknown): unknown {
  try {
    return JSON.parse(JSON.stringify(payload)) as unknown;
  } catch {
    return null;
  }
}

export function readPersistedSessionState<T>(key: string): T | null {
  if (!hasSessionStorage()) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function writePersistedSessionState(
  key: string,
  payload: unknown,
): void {
  if (!hasSessionStorage()) {
    return;
  }

  const plain = toPlainClone(payload);
  if (plain === null) {
    return;
  }

  try {
    window.sessionStorage.setItem(key, JSON.stringify(plain));
  } catch {
    // Storage unavailable or over quota
  }
}

export function clearPersistedSessionState(key: string): void {
  if (!hasSessionStorage()) {
    return;
  }

  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable
  }
}
